import React, { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Fields displayed per user_type. Ordered; "key" maps to admin field id saved on backend.
const FIELDSETS = {
  doctor: [
    { key: 'name', label: 'Name', get: (u) => `Dr. ${u.first_name || ''} ${u.last_name || ''}`.trim() },
    { key: 'profile_image', label: 'Profile Picture', type: 'image' },
    { key: 'email', label: 'Email', get: (u) => u.email },
    { key: 'phone', label: 'Phone', get: (u) => u.phone },
    { key: 'date_of_birth', label: 'Date of Birth', get: (u) => u.date_of_birth || '—' },
    { key: 'gender', label: 'Gender', get: (u) => u.gender || '—' },
    { key: 'license', label: 'License Document', type: 'license' },
    { key: 'license_number', label: 'License Number', get: (u) => u.license_number || '—' },
    { key: 'qualification', label: 'Qualification', get: (u) => u.qualification || '—' },
    { key: 'specialization', label: 'Specialization', get: (u) => u.specialization || '—' },
    { key: 'bio', label: 'Bio', get: (u) => u.bio || '—', multiline: true },
  ],
  provider: [
    { key: 'name', label: 'Facility Name', get: (u) => u.facility_name || u.name || '—' },
    { key: 'profile_image', label: 'Profile Picture', type: 'image' },
    { key: 'gallery', label: 'Facility Gallery', type: 'gallery' },
    { key: 'email', label: 'Email', get: (u) => u.email },
    { key: 'phone', label: 'Phone', get: (u) => u.phone },
    { key: 'license', label: 'License Document', type: 'license' },
    { key: 'license_number', label: 'License Number', get: (u) => u.license_number || '—' },
    { key: 'address', label: 'Address', get: (u) => u.address || '—' },
    { key: 'governorate', label: 'Governorate', get: (u) => u.governorate || '—' },
    { key: 'area', label: 'Area', get: (u) => u.area || '—' },
    { key: 'google_maps_link', label: 'Google Maps Location', type: 'link', get: (u) => u.google_maps_link },
    { key: 'services', label: 'Services', get: (u) => (u.services || []).join(', ') || '—' },
    { key: 'specialities', label: 'Specialties', get: (u) => (u.specialities || []).join(', ') || '—' },
    { key: 'description', label: 'Description', get: (u) => u.description || '—', multiline: true },
    { key: 'website', label: 'Website', type: 'link', get: (u) => u.website },
  ],
};

const STATUS_STYLES = {
  approved: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300',
  rejected: 'bg-rose-100 text-rose-800 ring-1 ring-rose-300',
  pending: 'bg-slate-100 text-slate-700 ring-1 ring-slate-300',
};

const absolute = (url) => (url && url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

const ImageLightbox = ({ src, alt, onClose }) => (
  <div
    className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
    onClick={onClose}
    data-testid="verification-lightbox"
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300"
      aria-label="Close"
    >
      &times;
    </button>
    <img src={src} alt={alt} className="max-h-full max-w-full rounded-lg shadow-2xl" />
  </div>
);

const LicensePreview = ({ license, onOpen }) => {
  if (!license) {
    return (
      <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800">
        ⚠ No license document uploaded by user yet
      </div>
    );
  }
  const url = absolute(license.url);
  if (license.content_type === 'application/pdf') {
    return (
      <div className="flex items-center gap-3">
        <div className="w-14 h-16 rounded bg-rose-100 border border-rose-300 flex items-center justify-center text-rose-700 text-xs font-bold">
          PDF
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline text-sm font-medium"
          data-testid="verification-license-pdf-link"
        >
          Open license (PDF) ↗
        </a>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(url)}
      className="block rounded-md overflow-hidden border border-gray-200 hover:ring-2 hover:ring-blue-400 transition"
      data-testid="verification-license-image"
    >
      <img src={url} alt="License" className="w-32 h-32 object-cover" />
    </button>
  );
};

const FieldActions = ({ status, onApprove, onReject, onReset, noteOpen, onToggleNote }) => (
  <div className="flex items-center gap-1 shrink-0">
    <button
      type="button"
      onClick={onApprove}
      className={`px-2 py-1 text-xs rounded font-medium transition ${
        status === 'approved' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
      }`}
      title="Approve"
      data-testid="field-approve-btn"
    >
      ✓
    </button>
    <button
      type="button"
      onClick={onReject}
      className={`px-2 py-1 text-xs rounded font-medium transition ${
        status === 'rejected' ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
      }`}
      title="Reject"
      data-testid="field-reject-btn"
    >
      ✗
    </button>
    <button
      type="button"
      onClick={onToggleNote}
      className={`px-2 py-1 text-xs rounded font-medium transition ${
        noteOpen ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
      }`}
      title="Add note"
      data-testid="field-note-btn"
    >
      📝
    </button>
    {status !== 'pending' && (
      <button
        type="button"
        onClick={onReset}
        className="px-2 py-1 text-xs rounded font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
        title="Reset to pending"
        data-testid="field-reset-btn"
      >
        ↺
      </button>
    )}
  </div>
);

const VerificationField = ({ field, user, fv, onChange, onLightbox }) => {
  const status = (fv || {}).status || 'pending';
  const note = (fv || {}).note || '';
  const [showNote, setShowNote] = useState(Boolean(note));
  const [draft, setDraft] = useState(note);

  const renderValue = () => {
    if (field.type === 'image') {
      const src = absolute(user.profile_image_url);
      if (!src) {
        return (
          <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs">
            No picture
          </div>
        );
      }
      return (
        <button type="button" onClick={() => onLightbox(src)} data-testid="verification-profile-image">
          <img
            src={src}
            alt="Profile"
            className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 hover:ring-2 hover:ring-blue-400 transition"
          />
        </button>
      );
    }
    if (field.type === 'gallery') {
      const urls = (user.gallery_urls || []).map(absolute);
      if (urls.length === 0) {
        return <div className="text-sm text-gray-400 italic">No facility pictures uploaded</div>;
      }
      return (
        <div className="flex flex-wrap gap-2" data-testid="verification-gallery">
          {urls.map((u) => (
            <button key={u} type="button" onClick={() => onLightbox(u)}>
              <img
                src={u}
                alt="Facility"
                className="w-20 h-20 object-cover rounded-md border border-gray-200 hover:ring-2 hover:ring-blue-400 transition"
              />
            </button>
          ))}
        </div>
      );
    }
    if (field.type === 'license') {
      return <LicensePreview license={user.license} onOpen={onLightbox} />;
    }
    if (field.type === 'link') {
      const url = field.get(user);
      if (!url) return <span className="text-sm text-gray-400 italic">Not provided</span>;
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm break-all">
          {url} ↗
        </a>
      );
    }
    const value = field.get ? field.get(user) : '—';
    return (
      <div className={`text-sm text-gray-800 ${field.multiline ? 'whitespace-pre-wrap' : ''} break-words`}>{value}</div>
    );
  };

  const setStatus = (newStatus, n) => onChange(field.key, newStatus, n);

  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{field.label}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[status]}`}>
              {status}
            </span>
          </div>
          {renderValue()}
        </div>
        <FieldActions
          status={status}
          onApprove={() => setStatus('approved', note || null)}
          onReject={() => setStatus('rejected', note || null)}
          onReset={() => setStatus('pending', null)}
          noteOpen={showNote}
          onToggleNote={() => setShowNote((v) => !v)}
        />
      </div>
      {showNote && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a note (visible to the user in a future update)…"
            className="flex-1 text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            data-testid="field-note-input"
          />
          <button
            type="button"
            onClick={() => setStatus(status, draft || null)}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
            data-testid="field-note-save-btn"
          >
            Save note
          </button>
        </div>
      )}
    </div>
  );
};

const VerificationReviewCard = ({ user, adminToken, onApproveAll, onRejectAll }) => {
  const [entry, setEntry] = useState(user);
  const [lightbox, setLightbox] = useState(null);
  const [saving, setSaving] = useState(false);

  const fieldset = FIELDSETS[entry.user_type] || [];
  const progress = entry.verification_progress || { approved_count: 0, required_count: 0, can_verify: false, required_fields: [] };

  const updateField = async (fieldKey, status, note) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/verification/${entry.id}/field`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ field: fieldKey, status, note }),
      });
      if (!res.ok) throw new Error('Failed to update field');
      const data = await res.json();
      setEntry((prev) => ({
        ...prev,
        field_verifications: data.field_verifications,
        verification_progress: data.verification_progress,
      }));
    } catch (err) {
      alert('Failed to save field decision: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const displayName = entry.user_type === 'doctor'
    ? `Dr. ${entry.first_name || ''} ${entry.last_name || ''}`.trim()
    : (entry.facility_name || entry.name || 'Unnamed');

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden" data-testid="verification-review-card">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-gray-900 truncate">{displayName}</h3>
            <div className="mt-1 flex flex-wrap gap-2 items-center">
              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                {entry.user_type === 'doctor'
                  ? (entry.specialization || 'Doctor')
                  : (entry.category || entry.facility_type || 'Provider')}
              </span>
              <span className="text-xs text-gray-500">
                Registered {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
              </span>
              <span className="text-xs text-gray-500 font-mono">{entry.id.slice(0, 8)}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-xs font-medium text-gray-600">
              {progress.approved_count} / {progress.required_count} required fields approved
            </span>
            <div className="w-40 h-2 rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full transition-all ${progress.can_verify ? 'bg-emerald-500' : 'bg-blue-500'}`}
                style={{ width: `${progress.required_count ? (progress.approved_count / progress.required_count) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fields grid */}
      <div className="px-5 py-2 divide-y-0">
        {fieldset.map((f) => (
          <VerificationField
            key={f.key}
            field={f}
            user={entry}
            fv={(entry.field_verifications || {})[f.key]}
            onChange={updateField}
            onLightbox={setLightbox}
          />
        ))}
      </div>

      {/* Action bar */}
      <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          {progress.can_verify ? (
            <span className="text-emerald-700 font-medium">✓ All required fields approved — ready to verify</span>
          ) : (
            <span>
              Missing approval for:{' '}
              <span className="font-medium text-gray-800">
                {progress.required_fields.filter(f => !(progress.approved_fields || []).includes(f)).join(', ') || '—'}
              </span>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onRejectAll(entry.id)}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            data-testid="verification-reject-all-btn"
          >
            Reject & Block
          </button>
          <button
            type="button"
            onClick={() => onApproveAll(entry.id)}
            disabled={!progress.can_verify || saving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="verification-approve-all-btn"
          >
            Approve & Verify
          </button>
        </div>
      </div>

      {lightbox && <ImageLightbox src={lightbox} alt="Preview" onClose={() => setLightbox(null)} />}
    </div>
  );
};

export default VerificationReviewCard;
